function yesNo(value) {
  return value ? "Yes" : "No";
}

function mark(value) {
  return value ? "X" : "";
}

function formatPhone(value = "") {
  const digits = String(value || "").replace(/\D/g, "").slice(0, 10);
  if (!digits) return "";
  if (digits.length < 4) return `(${digits}`;
  if (digits.length < 7) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
}

function buildAddress(patient = {}) {
  const street =
    patient.streetAddress ||
    patient.addressLine1 ||
    patient.address ||
    "";

  const city = patient.city || "";
  const state = patient.state || "";
  const zip = patient.zip || patient.postalCode || "";

  const cityState = [city, state].filter(Boolean).join(", ");
  const line2 = [cityState, zip].filter(Boolean).join(" ");

  const combined = [street, line2].filter(Boolean).join(", ");
  return combined || "";
}

function safe(value) {
  return value ?? "";
}

export function buildIncidentReportTemplateData({
  patient,
  encounter,
  incidentForm,
  medicalForm,
  signatureForm,
}) {
  const claimantType = incidentForm?.claimantType || "";
  const incidentLocation = incidentForm?.location || {};
  const incidentCauses = incidentForm?.causes || {};
  const conditions = incidentForm?.conditions || {};

  const witnessPresent =
    Boolean(incidentForm?.witnessName) ||
    Boolean(incidentForm?.witnessAddress) ||
    Boolean(incidentForm?.witnessPhone);

  const playerInvolved =
    Boolean(incidentForm?.playerInvolved) ||
    Boolean(incidentCauses?.struckByBall && incidentForm?.playerName);

  const otherClaimantType = claimantType === "Other" ? safe(incidentForm?.otherClaimantType) : "";
  const vendorName = claimantType === "Vendor" ? safe(incidentForm?.vendorName) : "";

  const descriptionOfIncident =
    incidentForm?.descriptionOfIncident ||
    encounter?.chiefComplaint ||
    encounter?.notes ||
    "";

  const firstAidGiven =
  medicalForm?.firstAidGiven === true ||
  incidentForm?.firstAidGiven === true;
  const disposition = medicalForm?.disposition || "";

  return {
    // claim info
    ClaimantName: safe(patient?.fullName),
    ClaimantAddress: buildAddress(patient),
    ClaimantEmail: patient?.email || "", 
    ClaimantPhone: formatPhone(patient?.phone || ""),
    Spectator: mark(claimantType === "Spectator"),
    Volunteer: mark(claimantType === "Volunteer"),
    Staff: mark(claimantType === "Staff"),
    Vendor: mark(claimantType === "Vendor"),
    Player: mark(claimantType === "Player"),
    OtherClaimant: mark(claimantType === "Other"),
    Othertype: otherClaimantType,
    VendorName: vendorName,

    // encounter basics
    DateOfIncident: safe(encounter?.arrivalDate),
    TimeOfIncident: safe(encounter?.arrivalTime),
    ReportCompletedBy: safe(encounter?.reportCompletedBy),
    ProviderName: safe(encounter?.providerName),
    DescriptionofIncident: descriptionOfIncident,

    // causes
    SlipFall: mark(Boolean(incidentCauses?.slipFall)),
    TripFall: mark(Boolean(incidentCauses?.tripFall)),
    Vehicle: mark(Boolean(incidentCauses?.vehicle)),
    StruckByAgainst: mark(Boolean(incidentCauses?.struckByAgainst)),
    SunHeat: mark(Boolean(incidentCauses?.sunHeat)),
    FoodBeverage: mark(Boolean(incidentCauses?.foodBeverage)),
    InsectBiteSting: mark(Boolean(incidentCauses?.insectBiteSting)),
    GolfCart: mark(Boolean(incidentCauses?.golfCart)),
    StruckByBall: mark(Boolean(incidentCauses?.struckByBall)),

    GolfCartNumber: safe(incidentForm?.golfCartNumber),
    GolfCartDriverName: safe(incidentForm?.golfCartDriverName),
    CompanyName: safe(incidentForm?.companyName),
    YesNo: yesNo(playerInvolved),
    PlayerName: playerInvolved ? safe(incidentForm?.playerName) : "N/A",

    // location
    Hole: mark(Boolean(incidentLocation?.hole)),
    Clubhouse: mark(Boolean(incidentLocation?.clubhouse)),
    Hospitality: mark(Boolean(incidentLocation?.hospitality)),
    Tee: mark(Boolean(incidentLocation?.tee)),
    Restroom: mark(Boolean(incidentLocation?.restroom)),
    Fairway: mark(Boolean(incidentLocation?.fairway)),
    ConcessionArea: mark(Boolean(incidentLocation?.concessionArea)),
    Green: mark(Boolean(incidentLocation?.green)),
    ParkingLot: mark(Boolean(incidentLocation?.parkingLot)),
    CartPath: mark(Boolean(incidentLocation?.cartPath)),
    Stairs: mark(Boolean(incidentLocation?.stairs)),
    OtherLocation: mark(Boolean(incidentLocation?.other)),
    Describe: safe(incidentForm?.otherLocationDescription),
    HoleNumber:
  incidentForm?.holeNumber ||
  incidentForm?.HoleNumber ||
  incidentForm?.hole ||
  incidentForm?.location?.holeNumber ||
  incidentForm?.location?.HoleNumber ||
  incidentForm?.location?.hole ||
  "",

    // photos / witness / other
    PhotosTaken: yesNo(Boolean(incidentForm?.photosTaken)),
    OtherInformation: safe(incidentForm?.otherInformation),
    WitnessName: witnessPresent ? safe(incidentForm?.witnessName) : "N/A",
    WitnessAddress: witnessPresent ? safe(incidentForm?.witnessAddress) : "N/A",
    WitnessPhone: witnessPresent ? safe(incidentForm?.witnessPhone) : "N/A",

    // medical
    FirstAidGiven: yesNo(firstAidGiven),
    BodyPartInjured: safe(medicalForm?.bodyPartInjured),
    TreatmentRendered: safe(medicalForm?.planTreatmentDisposition),

    // disposition
    Dismissed: mark(disposition === "dismissed"),
    ReferredtoPhysician: mark(disposition === "referredToPhysician"),
    SenttoHospital: mark(disposition === "sentToHospital"),
    PersonalCar: mark(disposition === "personalCar"),
    RescueUnit: mark(disposition === "rescueUnit"),
    DeclinedTreatment: mark(disposition === "declinedTreatment"),

    // conditions
    Rain: mark(Boolean(conditions?.rain)),
    Dew: mark(Boolean(conditions?.dew)),
    Dry: mark(Boolean(conditions?.dry)),
    Dark: mark(Boolean(conditions?.dark)),
    OtherCondition: mark(Boolean(conditions?.other)),
    OtherConditionText: safe(incidentForm?.otherConditionText),

    // signatures
    ReportCompletedBySignature: safe(signatureForm?.providerSignature),
    Signature: safe(signatureForm?.patientSignature),
  };
}